import { useLayoutEffect, useRef, useState } from "react";

// USAGE:
// const { registerRow, activeIndex, colWidths, colCardWidths, containerRect, onScrollSync } =
//   useScrollspyStickyHeader({ rootRef, rowCount, getHeaderCells, deps });
//
// <div ref={rootRef} style={{ overflow: "auto" }}>
//   {/* onScrollSync goes on whichever element actually scrolls
//       HORIZONTALLY — that's often rootRef itself, but not always: it
//       reads e.currentTarget.scrollLeft, not rootRef, specifically because
//       in at least one real caller the vertical scroll root and the
//       horizontal scroll element are two different nested nodes. */}
//   <div onScroll={onScrollSync} style={{ overflowX: "auto" }}>
//     {rows.map((row, i) => <Row key={i} ref={registerRow(i)} .../>)}
//   </div>
// </div>
// {activeIndex !== null && (
//   <div style={{ position: "fixed", top: containerRect.top, left: containerRect.left, width: containerRect.width }}>
//     {/* grid-template-columns from colWidths (pitch), each cell's OWN
//         width explicitly set from colCardWidths[i] — see the note below
//         on why a fixed CSS margin can't stand in for this. */}
//   </div>
// )}
//
// Sibling to useStickyGridHeader.js (read that file for the full "why JS
// instead of CSS `position: sticky`" writeup — same underlying CSS trap).
// This one is for a DIFFERENT shape of problem: useStickyGridHeader pins a
// single, always-or-never header (root: the browser viewport, clone always
// at top: 0). This hook is for content with MULTIPLE header sections
// stacked vertically (e.g. one per week in a month grid) inside a
// genuinely bounded, internally-scrolling container (not the page) — the
// pinned header needs to SWAP to whichever section is currently in view as
// you scroll, like Gmail's date-group headers ("scrollspy").
//
// Deliberately NOT built on IntersectionObserver + sentinels the way
// useStickyGridHeader is: IntersectionObserver only delivers entries for
// targets whose ratio actually changed, so tracking "which sections have I
// scrolled past" means merging batches into persistent state — a merge
// that's easy to get subtly wrong, and where a wrong index survives across
// content changes (e.g. navigating from a 6-row month to a 4-row one) since
// the state lives independently of the DOM. With only a handful of rows
// (a month has at most ~6 weeks), it's simpler and self-healing to just
// recompute from the real DOM on every scroll tick (rAF-throttled): there's
// no persisted state to go stale, `activeIndex` is naturally clamped to
// whatever rows currently exist because it's derived by walking
// `rowRefs.current` fresh each time.
const numbersEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

export function useScrollspyStickyHeader({ rootRef, rowCount, getHeaderCells, deps = [] }) {
  const rowRefs = useRef([]);
  // Stable per-index ref callbacks (cached in a Map) — an inline
  // `ref={(el) => rowRefs.current[i] = el}` in a .map() creates a NEW
  // function every render, which makes React detach+reattach the ref on
  // every re-render (not just real mount/unmount), churning far more than
  // necessary and risking races with the scroll handler reading stale refs.
  const registerRowCache = useRef(new Map());
  const registerRow = (index) => {
    if (!registerRowCache.current.has(index)) {
      registerRowCache.current.set(index, (el) => {
        rowRefs.current[index] = el;
      });
    }
    return registerRowCache.current.get(index);
  };

  const [activeIndex, setActiveIndex] = useState(null);
  const [colWidths, setColWidths] = useState([]);
  const [colCardWidths, setColCardWidths] = useState([]);
  const [containerRect, setContainerRect] = useState({ top: 0, left: 0, width: 0 });

  // Scrollspy + container-rect + column-widths, all in ONE recompute cycle.
  // These three used to be split across two effects (position/activeIndex
  // driven by scroll, colWidths driven by activeIndex-change + its own
  // ResizeObserver) — two independently-triggered measurements that could
  // fire at slightly different moments and disagree, confirmed in testing:
  // colWidths would occasionally reflect an ever-so-slightly stale layout
  // relative to what containerRect/activeIndex saw a moment later, showing
  // up as a small but real per-column drift. Computing all three from the
  // SAME rects, in the SAME tick, removes the possibility of that gap entirely.
  //
  // colWidths entries are each column's PITCH to the next one (its own
  // width + whatever margin/gap follows it), not just its own rendered
  // width — a naive per-cell width undercounts real spacing (confirmed
  // empirically: consecutive header cells' own width measured ~185px, but
  // their left edges were ~197px apart), so this reads the true delta
  // straight from the DOM instead of trying to separately derive width +
  // gap and hoping they recombine correctly. The clone's CSS should size
  // each column track (grid-template-columns) to these pitches directly and
  // NOT add its own gap on top (that would double-count the spacing this
  // already includes).
  //
  // colCardWidths is the SEPARATE real rendered width of each card cell —
  // used to give each clone cell an explicit `width` inside its (wider,
  // pitch-sized) track, rather than a fixed CSS margin standing in for
  // "whatever the gap is." A fixed margin can't work here: how much of the
  // pitch is "visible card" vs. "gap" isn't constant across viewport
  // widths — confirmed at ~1000px, where the real row's own
  // `max-width: calc((100vw - 10rem - 6*0.5rem)/7)` formula produces a
  // narrower card than the grid's `minmax(12rem, 1fr)` floor would
  // otherwise allow, so the card stops filling its track well before wider
  // viewports would. Re-measuring both values on every recompute (not just
  // once) is what makes the header track the content's actual responsive
  // width instead of whatever it happened to be on first render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `deps` is a caller-supplied re-measure trigger list, not values read in this closure
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let rafId = null;
    const recompute = () => {
      rafId = null;
      const rootTop = root.getBoundingClientRect().top;
      let next = null;
      for (let i = 0; i < rowCount; i++) {
        const row = rowRefs.current[i];
        const headerTop = row?.querySelector(".day-header")?.getBoundingClientRect().top;
        if (headerTop !== undefined && headerTop <= rootTop) next = i;
      }
      setActiveIndex((prev) => (prev === next ? prev : next));

      const rect = root.getBoundingClientRect();
      setContainerRect((prev) =>
        prev.top === rect.top && prev.left === rect.left && prev.width === rect.width
          ? prev
          : { top: rect.top, left: rect.left, width: rect.width }
      );

      const activeRow = next != null ? rowRefs.current[next] : null;
      const cells = activeRow ? getHeaderCells(activeRow) : [];
      if (!cells.length) {
        setColWidths((prev) => (prev.length ? [] : prev));
        setColCardWidths((prev) => (prev.length ? [] : prev));
      } else {
        const rects = cells.map((cell) => cell.getBoundingClientRect());
        const n = rects.length;
        // The last column has no "next" cell to measure a real pitch
        // against — falling back to its own raw width (no trailing
        // gap-worth of space included) made it visibly narrower than every
        // other column, which measured with the gap folded in. Reuse the
        // previous column's pitch instead: spacing has been uniform across
        // every column in testing, so it's a reliable stand-in for "this
        // column's pitch would have been, had there been one more after it."
        const lastPitch = n > 1 ? rects[n - 1].left - rects[n - 2].left : rects[0].width;
        const nextColWidths = rects.map((r, i) => (i < n - 1 ? rects[i + 1].left - r.left : lastPitch));
        const nextColCardWidths = rects.map((r) => r.width);
        // Same bail-if-unchanged guard as activeIndex/containerRect above —
        // this runs every scroll frame regardless of whether the active row
        // changed (needed to also catch resizes while staying on the same
        // row), so without it every frame would create new arrays and
        // trigger a re-render even when nothing actually moved.
        setColWidths((prev) => (numbersEqual(prev, nextColWidths) ? prev : nextColWidths));
        setColCardWidths((prev) => (numbersEqual(prev, nextColCardWidths) ? prev : nextColCardWidths));
      }
    };
    const scheduleRecompute = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(recompute);
    };

    recompute();
    root.addEventListener("scroll", scheduleRecompute, { passive: true });
    // Capture phase — a scroll on some OUTER page container wouldn't bubble
    // through `root`, but window-level listeners in the capture phase still
    // see it. Covers the edge case where the page itself can also scroll
    // (not just this bounded container), which would otherwise leave
    // containerRect stale.
    window.addEventListener("scroll", scheduleRecompute, { passive: true, capture: true });
    window.addEventListener("resize", scheduleRecompute);
    return () => {
      root.removeEventListener("scroll", scheduleRecompute);
      window.removeEventListener("scroll", scheduleRecompute, { capture: true });
      window.removeEventListener("resize", scheduleRecompute);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [rowCount, ...deps]);

  const stickyHeadRef = useRef(null);
  // Takes the scroll event itself rather than reading rootRef.current — the
  // element that actually scrolls horizontally isn't always rootRef (the
  // vertical scroll container passed in for the scrollspy calc above): in
  // Calendar Log those are two different nested elements
  // (.calendar-month-wrapper vs .log-view-weeks), so hardcoding rootRef
  // here previously left the clone's horizontal position stuck at 0 —
  // confirmed the actual bug behind a real "columns misaligned" report.
  const onScrollSync = (e) => {
    if (stickyHeadRef.current) {
      stickyHeadRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  return { registerRow, stickyHeadRef, activeIndex, colWidths, colCardWidths, containerRect, onScrollSync };
}

export default useScrollspyStickyHeader;
